import type { DateRange } from 'react-day-picker';

const DEFAULT_LOCALE = 'en-US';

const ISO_DATE_FORMATTER = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
});

const MEDIUM_DATE_FORMATTER = new Intl.DateTimeFormat(DEFAULT_LOCALE, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
});

const LONG_DATE_FORMATTER = new Intl.DateTimeFormat(DEFAULT_LOCALE, {
    year: 'numeric',
    month: 'long',
    day: '2-digit',
});

const WEEKDAY_LONG = new Intl.DateTimeFormat(DEFAULT_LOCALE, { weekday: 'long' });
const WEEKDAY_SHORT = new Intl.DateTimeFormat(DEFAULT_LOCALE, { weekday: 'short' });
const MONTH_SHORT = new Intl.DateTimeFormat(DEFAULT_LOCALE, { month: 'short' });
const MONTH_SHORT_YEAR = new Intl.DateTimeFormat(DEFAULT_LOCALE, {
    month: 'short',
    year: 'numeric',
});
const MONTH_SHORT_DAY = new Intl.DateTimeFormat(DEFAULT_LOCALE, {
    month: 'short',
    day: 'numeric',
});

/** 12-hour clock time: "2:42 PM". Pinned to en-US — the host locale would
 *  otherwise decide the day-period casing ("pm" under en-IN, en-GB, …). */
const TIME_FORMATTER = new Intl.DateTimeFormat(DEFAULT_LOCALE, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
});

const RELATIVE_FORMATTER = new Intl.RelativeTimeFormat(DEFAULT_LOCALE, { numeric: 'auto' });

function toDate(input: Date | string): Date {
    return input instanceof Date ? input : new Date(input);
}

function pad2(value: number): string {
    return value.toString().padStart(2, '0');
}

function ordinalSuffix(day: number): string {
    const mod100 = day % 100;
    if (mod100 >= 11 && mod100 <= 13) return 'th';
    switch (day % 10) {
        case 1:
            return 'st';
        case 2:
            return 'nd';
        case 3:
            return 'rd';
        default:
            return 'th';
    }
}

function startOfDay(date: Date): Date {
    const copy = new Date(date);
    copy.setHours(0, 0, 0, 0);
    return copy;
}

// ── Simple helpers ────────────────────────────────────────────────────────

export const getExpiryDate = (expiresIn: number): string => {
    return new Date(Date.now() + expiresIn * 1000).toISOString();
};

export const formatDateTimeToScheduleX = (date: Date | string): string => {
    const d = toDate(date);
    return (
        `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ` +
        `${pad2(d.getHours())}:${pad2(d.getMinutes())}`
    );
};

export const normalizeTimeFormat = (time: string): string => {
    if (!time) return time;
    if (time.split(':').length === 3) {
        return time.split(':').slice(0, 2).join(':');
    }
    return time;
};

// ── Date formatters (Intl-based) ──────────────────────────────────────────

/**
 * Medium date format: "Oct 23, 2025".
 * Default format for tables, profile fields, and list views.
 */
export const formatDate = (date: Date | string): string =>
    MEDIUM_DATE_FORMATTER.format(toDate(date));

/** Medium date + 12-hour time: "Oct 23, 2025, 2:42 PM".
 *  Use in table cells where the time-of-day is meaningful (e.g. send time). */
export const formatDateWithTime = (date: Date | string | null | undefined): string => {
    if (!date) return '—';
    const d = toDate(date);
    if (Number.isNaN(d.getTime())) return '—';
    return `${formatDate(d)}, ${TIME_FORMATTER.format(d)}`;
};

/** Cache per zone — `Intl.DateTimeFormat` construction is the expensive part, and
 *  these render inside table cells and occurrence lists. */
const ZONED_DATE_TIME_FORMATTERS = new Map<string, Intl.DateTimeFormat>();

function zonedDateTimeFormatter(timeZone: string): Intl.DateTimeFormat | null {
    const cached = ZONED_DATE_TIME_FORMATTERS.get(timeZone);
    if (cached) return cached;
    try {
        const formatter = new Intl.DateTimeFormat(DEFAULT_LOCALE, {
            timeZone,
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
        });
        ZONED_DATE_TIME_FORMATTERS.set(timeZone, formatter);
        return formatter;
    } catch {
        // Not a valid IANA id (the column is only length-validated server-side).
        return null;
    }
}

/**
 * Medium date + 12-hour time rendered in an explicit IANA zone:
 * "Oct 23, 2025, 9:00 AM".
 *
 * Use wherever the instant belongs to a schedule the user configured in a specific
 * timezone — recurring-campaign occurrences, `nextRunAt` — so the displayed time
 * matches the zone they picked rather than whatever zone their browser happens to
 * be in. Falls back to browser-local formatting when `timeZone` is missing or
 * unrecognised, so a bad value degrades instead of blanking the row.
 */
export const formatDateWithTimeInZone = (
    date: Date | string | null | undefined,
    timeZone: string | null | undefined,
): string => {
    if (!date) return '—';
    const d = toDate(date);
    if (Number.isNaN(d.getTime())) return '—';
    if (!timeZone) return formatDateWithTime(d);
    const formatter = zonedDateTimeFormatter(timeZone);
    if (!formatter) return formatDateWithTime(d);
    return formatter.format(d);
};

/**
 * 12-hour clock time-of-day: "9:45 AM" / "10:23 PM". Day-level context is
 * expected to come from a separate divider/label, so this shows only the time.
 * Returns '' for empty or unparseable input.
 */
export const formatTimeOfDay = (date: Date | string | null | undefined): string => {
    if (!date) return '';
    const d = toDate(date);
    if (Number.isNaN(d.getTime())) return '';
    return TIME_FORMATTER.format(d);
};

/**
 * Day-relative time: "Today at 9:00 AM" / "Tomorrow at 9:00 AM" / "Jul 18 at
 * 9:00 AM". For upcoming moments where the *day* matters more than the date —
 * pair it with `formatDate()` when the exact date still needs to be readable.
 * Returns '' for empty or unparseable input.
 */
export const formatDayAtTime = (date: Date | string | null | undefined): string => {
    if (!date) return '';
    const d = toDate(date);
    if (Number.isNaN(d.getTime())) return '';

    // Day-boundary delta, not an hour count: 11pm → 1am next day is "Tomorrow".
    const msPerDay = 24 * 60 * 60 * 1000;
    const daysAhead = Math.round(
        (startOfDay(d).getTime() - startOfDay(new Date()).getTime()) / msPerDay,
    );

    const day =
        daysAhead === 0 ? 'Today' : daysAhead === 1 ? 'Tomorrow' : MONTH_SHORT_DAY.format(d);
    return `${day} at ${formatTimeOfDay(d)}`;
};

/** ISO-ish date: "2025-10-23". */
export const formatDateISO = (date: Date | string): string =>
    ISO_DATE_FORMATTER.format(toDate(date));

/** "23, Oct 2025" — day-first short month. */
export const formatDateWithMonthName = (date: Date | string): string => {
    const d = toDate(date);
    return `${pad2(d.getDate())}, ${MONTH_SHORT.format(d)} ${d.getFullYear()}`;
};

/**
 * "12th May 2026" — ordinal day + full month name. Used on profile and member
 * cards where a friendly, fully-spelled-out date reads better than the short
 * MEDIUM_DATE_FORMATTER output.
 */
const MONTH_LONG = new Intl.DateTimeFormat(DEFAULT_LOCALE, { month: 'long' });
export const formatDateOrdinalLong = (date: Date | string | null | undefined): string => {
    if (!date) return '—';
    const d = toDate(date);
    if (Number.isNaN(d.getTime())) return '—';
    const day = d.getDate();
    return `${day}${ordinalSuffix(day)} ${MONTH_LONG.format(d)} ${d.getFullYear()}`;
};

/** Long form: "October 23, 2025". */
export const formatDateCalendar = (date: Date): string => LONG_DATE_FORMATTER.format(date);

/** Medium form alias — kept for backward compatibility with existing callers. */
export const formatDateShort = (date: Date | string): string => formatDate(date);

export const formatDateRange = (date: DateRange): string => {
    if (date.from && date.to) {
        return `${formatDateCalendar(date.from)} - ${formatDateCalendar(date.to)}`;
    }
    return formatDateCalendar(date.from || date.to || new Date());
};

// ── Diff / duration helpers ───────────────────────────────────────────────

/** Returns ms difference (date1 - date2). Mirrors the previous moment().diff() semantics. */
export const getDateDifference = (date1: Date | string, date2: Date | string): number => {
    return toDate(date1).getTime() - toDate(date2).getTime();
};

export const calculateDuration = (startedAt: Date | string, endedAt: Date | string): string => {
    const ms = toDate(endedAt).getTime() - toDate(startedAt).getTime();
    const totalSeconds = Math.max(0, Math.floor(ms / 1000));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (totalSeconds < 60) return `${seconds}s`;
    if (totalSeconds < 3600) return `${minutes}m ${seconds}s`;
    return `${hours}h ${minutes}m ${seconds}s`;
};

// ── Relative time / chat / notification helpers ──────────────────────────

/**
 * "Today" | "Yesterday" | weekday for <= 7d | "29th, Jan 2025" for older.
 */
export const formatChatDate = (date: Date | string): string => {
    const d = toDate(date);
    const msPerDay = 24 * 60 * 60 * 1000;
    const daysDiff = Math.floor(
        (startOfDay(new Date()).getTime() - startOfDay(d).getTime()) / msPerDay,
    );

    if (daysDiff === 0) return 'Today';
    if (daysDiff === 1) return 'Yesterday';
    if (daysDiff >= 2 && daysDiff <= 7) return WEEKDAY_LONG.format(d);

    const day = d.getDate();
    return `${day}${ordinalSuffix(day)}, ${MONTH_SHORT.format(d)} ${d.getFullYear()}`;
};

export const formatTimeAgo = (timestamp: Date | string): string => {
    const d = toDate(timestamp);
    const diffMs = Date.now() - d.getTime();
    const diffMinutes = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return RELATIVE_FORMATTER.format(-diffMinutes, 'minute');
    if (diffHours < 24) return RELATIVE_FORMATTER.format(-diffHours, 'hour');
    if (diffDays < 7) return RELATIVE_FORMATTER.format(-diffDays, 'day');
    return MONTH_SHORT_DAY.format(d);
};

/**
 * Gmail-style email timestamp.
 * Today: "14:31 (1 hour ago)"
 * This year: "Thu 19 Mar, 14:31 (10 days ago)"
 * Older: "Tue 10 Mar 2025, 14:31 (1 year ago)"
 */
export const formatEmailTimestamp = (date: Date | string): string => {
    const now = new Date();
    const d = toDate(date);
    const relative = formatTimeAgo(d);
    const time = `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;

    const isToday =
        d.getDate() === now.getDate() &&
        d.getMonth() === now.getMonth() &&
        d.getFullYear() === now.getFullYear();

    if (isToday) return `${time} (${relative})`;

    const weekday = WEEKDAY_SHORT.format(d);
    const month = MONTH_SHORT.format(d);
    const day = d.getDate();

    if (d.getFullYear() === now.getFullYear()) {
        return `${weekday} ${day} ${month}, ${time} (${relative})`;
    }
    return `${weekday} ${day} ${month} ${d.getFullYear()}, ${time} (${relative})`;
};

export const formatNotificationDate = (date: Date | string): string => {
    const d = toDate(date);
    const diffMs = Date.now() - d.getTime();
    const diffMinutes = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    const diffYears = Math.floor(diffDays / 365);

    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return RELATIVE_FORMATTER.format(-diffMinutes, 'minute');
    if (diffHours < 24) return RELATIVE_FORMATTER.format(-diffHours, 'hour');
    if (diffDays < 7) return WEEKDAY_LONG.format(d);
    if (diffYears < 1) return MONTH_SHORT_DAY.format(d);
    return MONTH_SHORT_YEAR.format(d);
};
